/** DNA: samples by where they are, lab orders, and returned result files. Pulled is not sent; sent is not back. */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { localDate, type JobDetail, type JobItemDTO, type JobSummary } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { RefPicker } from "../../../web/forms/pickers";
import { ApiError, api } from "../../../web/lib/api";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Field, List, Loading, Progress, Row, Section, Sheet, TextInput, cx, toast } from "../../../web/ui/kit";
import { CONDITION_RESULTS, DNA_TESTS, PARENT_OUTCOMES } from "../contract/events";
import { DNA_STATUS_LABELS } from "../shared/labels";
import type { DnaSampleRow } from "../shared/types";
import { GeneticsShell } from "./GeneticsWorkspace";
import { ImportPanel } from "./ImportPanel";
import { StageSteps } from "./ui";

interface DnaDTO { samples: DnaSampleRow[]; orders: JobSummary[]; imports: Array<{ id: string; name: string; rows: number; created_at: string; lab: string | null; open: number }> }

function resultLine(r: DnaSampleRow["result"]): string {
  if (!r) return "";
  if (!r.sample_ok) return t("Sample failed, collect again");
  const parts: string[] = [];
  if (r.sire) parts.push(`${t("Sire check")}: ${t(PARENT_OUTCOMES[r.sire])}`);
  if (r.dam) parts.push(`${t("Dam check")}: ${t(PARENT_OUTCOMES[r.dam])}`);
  for (const c of r.conditions.filter((x) => x.result !== "free")) parts.push(`${c.condition}: ${t(CONDITION_RESULTS[c.result])}`);
  if (r.genomic) parts.push(t("Genomic EPDs released"));
  return parts.join(" · ");
}

function SampleList({ rows }: { rows: DnaSampleRow[] }) {
  return (
    <List>
      {rows.map((s) => {
        const bad = s.status === "failed" || s.result?.sire === "excluded" || s.result?.dam === "excluded";
        return (
          <Row key={s.animal.id} icon="dna" tone={bad ? "warn" : "genetics"} to={`/cattle/${s.animal.id}`}
            title={<>{s.animal.label}{s.barcode && <span className="muted mono"> · {s.barcode}</span>}</>}
            meta={[s.lab, s.on ? formatDate(s.on) : null, resultLine(s.result)].filter(Boolean).join(" · ")}
            trailing={<Chip tone={bad ? "warn" : s.status === "resulted" ? "good" : "neutral"}>{t(DNA_STATUS_LABELS[s.status])}</Chip>} />
        );
      })}
    </List>
  );
}

export function StartDnaOrder({ open, onClose, animalIds = [] }: { open: boolean; onClose: () => void; animalIds?: string[] }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const year = me?.ranch ? localDate(new Date(), me.ranch.timezone).slice(0, 4) : new Date().getFullYear().toString();
  const [title, setTitle] = useState("");
  const [lab, setLab] = useState("");
  const [tests, setTests] = useState<string[]>(["parentage"]);
  const [group, setGroup] = useState<string | null>(null);
  const [animals, setAnimals] = useState<string[]>(animalIds);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      let ids = animals;
      if (group) ids = [...new Set([...ids, ...(await api.get<{ members: Array<{ id: string }> }>(`/groups/${group}`)).members.map((m) => m.id)])];
      const { id } = await api.post<{ id: string }>("/m/genetics/dna/orders", { title: title.trim() || t("{{year}} DNA", { year }), lab: lab.trim() || undefined, tests, animal_ids: ids });
      await invalidateAll();
      onClose();
      navigate(`/work/jobs/${id}`);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("Start a DNA order")}>
      <form onSubmit={(e) => { e.preventDefault(); void start(); }}>
        <Field label={t("Name")} optional><TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("{{year}} DNA", { year })} /></Field>
        <Field label={t("Lab")} optional><TextInput value={lab} onChange={(e) => setLab(e.target.value)} placeholder={"Neogen"} /></Field>
        <Field label={t("Tests")}>
          <div className="choice-chips">{Object.entries(DNA_TESTS).map(([k, v]) => <button key={k} type="button" className={cx("choice", tests.includes(k) && "choice-on")} aria-pressed={tests.includes(k)} onClick={() => setTests(tests.includes(k) ? tests.filter((x) => x !== k) : [...tests, k])}>{t(v)}</button>)}</div>
        </Field>
        <Field label={t("A whole group")} optional><RefPicker target="group" value={group} onChange={(v) => setGroup(v as string | null)} /></Field>
        <Field label={t("Or pick animals")} optional><RefPicker target="animal" multiple value={animals} onChange={(v) => setAnimals((v as string[]) ?? [])} /></Field>
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!group && !animals.length}>{t("Start")}</Button></div>
      </form>
    </Sheet>
  );
}

export function DnaPage() {
  useLanguage();
  const can = useCan();
  const q = useApi<DnaDTO>("/m/genetics/dna");
  const [starting, setStarting] = useState(false);
  const [resume, setResume] = useState<string | null>(null);
  const rows = q.data?.samples ?? [];
  const by = (s: string) => rows.filter((r) => r.status === s);
  const pulled = by("collected");
  return (
    <GeneticsShell title={t("DNA")} subtitle={t("Samples, lab orders and results.")} actions={can("record") && <Button variant="primary" icon="plus" onClick={() => setStarting(true)}>{t("Start a DNA order")}</Button>}>
      {q.isLoading ? <Loading /> : !q.data ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : (
        <>
          {pulled.length > 0 && (
            <Section title={t("Pulled, not sent")} count={pulled.length} action={can("record") && <Button size="sm" icon="send" onClick={() => openRecord({ initial: { type: "genetics.dna_sent", subjects: pulled.map((s) => ({ kind: "animal" as const, id: s.animal.id, role: "animal", label: s.animal.label })), data: { lab: pulled[0].lab } } })}>{t("Mark sent")}</Button>}>
              <SampleList rows={pulled} />
            </Section>
          )}
          {by("failed").length > 0 && <Section title={t("Failed, collect again")} count={by("failed").length}><SampleList rows={by("failed")} /></Section>}
          {by("sent").length > 0 && <Section title={t("At the lab")} count={by("sent").length}><SampleList rows={by("sent")} /></Section>}
          <Section title={t("Orders")} count={q.data.orders.length}>
            {q.data.orders.length ? <List>{q.data.orders.map((o) => <Row key={o.id} icon="dna" to={`/work/jobs/${o.id}`} title={o.title} meta={[o.status === "done" ? t("Finished") : null, plural(o.progress.total, "{{count}} animal", "{{count}} animals")].filter(Boolean).join(" · ")} trailing={o.progress.total ? <span className="muted">{o.progress.done}/{o.progress.total}</span> : undefined} />)}</List>
              : <p className="muted">{t("No DNA orders yet. Start one for a group of calves, or just record each sample as you pull it.")}</p>}
          </Section>
          {can("record") && (
            <Section title={t("Results from the lab")}>
              <Card>
                <Disclosure label={t("Bring in a results file")} defaultOpen={!by("resulted").length && by("sent").length > 0}>
                  <ImportPanel kind="dna" onDone={() => void q.refetch()} />
                </Disclosure>
              </Card>
              {q.data.imports.length > 0 && (
                <List>{q.data.imports.map((f) => <Row key={f.id} icon="file" title={f.name} meta={[f.lab, formatDate(f.created_at), plural(f.rows, "{{count}} row", "{{count}} rows")].filter(Boolean).join(" · ")}
                  trailing={f.open ? <Button size="sm" onClick={() => setResume(f.id)}>{plural(f.open, "{{count}} left to settle", "{{count}} left to settle")}</Button> : <Chip tone="good" icon="done">{t("Resolved")}</Chip>} />)}</List>
              )}
            </Section>
          )}
          {by("resulted").length > 0 && <Section title={t("Results back")} count={by("resulted").length}><SampleList rows={by("resulted")} /></Section>}
          {!rows.length && !q.data.orders.length && <Empty icon="dna" title={t("No DNA samples yet")} body={t("Say “pulled hair on the 2026 calves for DNA” or start an order.")} />}
        </>
      )}
      <StartDnaOrder open={starting} onClose={() => setStarting(false)} />
      <Sheet open={!!resume} onClose={() => setResume(null)} title={t("Settle the rest of the file")} size="lg">{resume && <ImportPanel kind="dna" resumeId={resume} onDone={() => void q.refetch()} />}</Sheet>
    </GeneticsShell>
  );
}

/** Job page for a DNA order: pull samples per animal, mark the order sent, bring the results in. */
export function DnaOrderJob({ job, items, refresh }: { job: JobDetail; items: JobItemDTO[]; refresh(): void }) {
  useLanguage();
  const can = useCan();
  const { data: me } = useMe();
  const today = me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10);
  const q = useApi<DnaDTO>("/m/genetics/dna");
  const [sending, setSending] = useState(false);
  const [on, setOn] = useState(today);
  const [orderNo, setOrderNo] = useState((job.data.order_number as string) ?? "");
  const [lab, setLab] = useState((job.data.lab as string) ?? "");
  const [busy, setBusy] = useState(false);
  const status = new Map((q.data?.samples ?? []).map((s) => [s.animal.id, s]));
  const live = items.filter((i) => i.status !== "skipped");
  const states = live.map((i) => status.get(i.subject_id)?.status ?? "none");
  const pulled = states.filter((s) => s === "collected").length;
  const record = (i: JobItemDTO) => openRecord({
    initial: { type: "genetics.dna_sample", subjects: [{ kind: "animal", id: i.subject_id, role: "animal", label: i.label }], job_id: job.id, data: { sample_type: "hair", lab: job.data.lab, tests: job.data.tests } },
    onDone: () => { refresh(); void q.refetch(); },
  });
  const markSent = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ sent: number }>(`/m/genetics/dna/orders/${job.id}/sent`, { on, order_number: orderNo || undefined, lab: lab || undefined });
      await invalidateAll();
      toast(plural(r.sent, "{{count}} sample marked sent", "{{count}} samples marked sent"), { tone: "good" });
      setSending(false);
      refresh();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return (
    <div className="stack">
      <StageSteps stages={["collecting", "sent", "results"]} current={job.stage} status={job.status} />
      <div className="genetics-row-meta">
        {job.data.lab ? <span>{String(job.data.lab)}</span> : null}
        {(job.data.tests as string[] | undefined)?.length ? <span>{(job.data.tests as Array<keyof typeof DNA_TESTS>).map((x) => t(DNA_TESTS[x] ?? x)).join(", ")}</span> : null}
        {job.data.order_number ? <span>{t("order {{number}}", { number: String(job.data.order_number) })}</span> : null}
      </div>
      <Progress done={states.filter((s) => s === "resulted" || s === "failed").length} total={live.length} label={t("Results back")} />
      {can("record") && (
        <div className="row-actions">
          {pulled > 0 && <Button variant="primary" icon="send" onClick={() => setSending(true)}>{plural(pulled, "Mark {{count}} sample sent", "Mark {{count}} samples sent")}</Button>}
          <Button icon="upload" to="/work/genetics/dna">{t("Bring in results")}</Button>
        </div>
      )}
      <ul className="worklist">
        {live.map((i) => {
          const s = status.get(i.subject_id);
          return (
            <li key={i.id} className={cx("work-item", s && s.status !== "collected" && "is-done")}>
              <Link to={`/cattle/${i.subject_id}`} className="work-subject">{i.label}</Link>
              <span className="work-status">{s ? <Chip tone={s.status === "failed" ? "warn" : s.status === "resulted" ? "good" : "neutral"}>{t(DNA_STATUS_LABELS[s.status])}</Chip> : <Chip tone="neutral">{t("No sample yet")}</Chip>}{s?.barcode && <span className="muted mono"> {s.barcode}</span>}</span>
              <span className="work-actions">{can("record") && (!s || s.status === "failed") && <Button size="sm" variant="primary" onClick={() => record(i)}>{t("Record sample")}</Button>}</span>
            </li>
          );
        })}
      </ul>
      <Sheet open={sending} onClose={() => setSending(false)} title={t("Mark samples sent")}>
        <form onSubmit={(e) => { e.preventDefault(); void markSent(); }}>
          <Field label={t("Sent on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
          <Field label={t("Lab")}><TextInput value={lab} onChange={(e) => setLab(e.target.value)} placeholder={"Neogen"} /></Field>
          <Field label={t("Order or case number")} optional><TextInput value={orderNo} onChange={(e) => setOrderNo(e.target.value)} /></Field>
          <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!lab.trim()}>{t("Mark sent")}</Button></div>
        </form>
      </Sheet>
    </div>
  );
}

